import { useEffect, useMemo, useState } from 'react'
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import { FormExperienceScreen } from '../Forms/FormExperienceScreen'
import { useAuth } from '../../context/useAuth'
import { useToast } from '../../context/useToast'
import { useFormDefinition, useFormDefinitions } from '../../hooks/useFormDefinitions'
import {
  DEBUG_MEMBER_FORM_DEFINITIONS,
  sendFormRequest,
  subscribeAssignedFormRequests,
  subscribeFormRequestMembers,
  subscribeSentFormRequests,
  type FormRequestMember,
} from '../../services/formRequestService'
import { isNoSignInPreviewActive } from '../../debug'
import { testingOverrideAllowsAllFeatures } from '../../config/testingOverrides'
import { setGhostEchoDirty } from '../../utils/ghostEchoDirtyRegistry'
import type { WorkspaceRole } from '../../context/WorkspaceContext'
import { roleCanPerform } from '../../utils/workspacePermissions'
import type { FormDefinition, FormRequest } from '../../types/forms'
import { Button, IconButton, Pressable, Select } from '../Common'
import { useNls } from '../../nls/nlsProvider'
import { formatRoleLabel } from '../../utils/roleLabels'

const formatRequestStatus = (request: FormRequest) => request.status.toLocaleLowerCase().replaceAll('_', ' ')

const EMPTY_MEMBERS: FormRequestMember[] = []
const EMPTY_SENT: FormRequest[] = []

export const FormsOrchestrator = ({ workspaceId, role }: { workspaceId: string; role: WorkspaceRole }) => {
  const { t } = useNls()
  const { currentUser } = useAuth()
  const { showToast } = useToast()
  const canAssign = roleCanPerform(role, 'EDITOR') || testingOverrideAllowsAllFeatures()
  const activeFormsPage = useFormDefinitions({ workspaceId, lifecycleStatus: 'ACTIVE', search: '', sort: 'NEWEST', cursor: null })
  const [members, setMembers] = useState<FormRequestMember[]>([])
  const [assigned, setAssigned] = useState<FormRequest[]>([])
  const [sent, setSent] = useState<FormRequest[]>([])
  const [selectedFormId, setSelectedFormId] = useState('')
  const [selectedRecipientId, setSelectedRecipientId] = useState('')
  const [sending, setSending] = useState(false)
  const [activeRequest, setActiveRequest] = useState<FormRequest | null>(null)
  const [activeFormDirty, setActiveFormDirty] = useState(false)
  const selectedDefinition = useFormDefinition(activeRequest?.formId ?? null)

  const activeForms = isNoSignInPreviewActive() ? DEBUG_MEMBER_FORM_DEFINITIONS : activeFormsPage.items
  const rendererDefinition = useMemo<FormDefinition | null>(() => {
    if (!activeRequest) return null
    return DEBUG_MEMBER_FORM_DEFINITIONS.find((form) => form.id === activeRequest.formId) ?? selectedDefinition.form
  }, [activeRequest, selectedDefinition.form])

  useEffect(() => {
    setGhostEchoDirty(Boolean(activeRequest && activeFormDirty))
    return () => setGhostEchoDirty(false)
  }, [activeFormDirty, activeRequest])

  const effectiveMembers = canAssign ? members : EMPTY_MEMBERS
  const effectiveSent = canAssign ? sent : EMPTY_SENT
  const effectiveSelectedFormId = selectedFormId || activeForms[0]?.id || ''
  const effectiveRecipientId = selectedRecipientId || effectiveMembers[0]?.uid || ''

  useEffect(() => {
    if (!currentUser) return
    const stopMembers = canAssign
      ? subscribeFormRequestMembers(workspaceId, currentUser.uid, setMembers, (error) => showToast(error.message, 'error'))
      : () => undefined
    const stopAssigned = subscribeAssignedFormRequests(workspaceId, currentUser.uid, setAssigned, (error) => showToast(error.message, 'error'))
    const stopSent = canAssign
      ? subscribeSentFormRequests(workspaceId, currentUser.uid, setSent, (error) => showToast(error.message, 'error'))
      : () => undefined
    return () => {
      stopMembers()
      stopAssigned()
      stopSent()
    }
  }, [canAssign, currentUser, showToast, workspaceId])

  const send = async () => {
    if (!effectiveSelectedFormId || !effectiveRecipientId) return
    setSending(true)
    try {
      await sendFormRequest({ workspaceId, formId: effectiveSelectedFormId, recipientId: effectiveRecipientId })
      showToast('Form assigned.', 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not assign form.', 'error')
    } finally {
      setSending(false)
    }
  }

  return (
    <section className="memberControlPanel" data-testid="member-forms-orchestrator">
      <header className="memberControlHeader">
        <div>
          <span className="memberEyebrow">Forms</span>
          <h2>Assignments</h2>
        </div>
      </header>
      {canAssign ? (
        <div className="memberAssignmentBar">
          <label>
            <span>Form</span>
            <Select ariaLabel="Member form assignment form" value={effectiveSelectedFormId} options={activeForms.map((form) => ({ value: form.id, label: form.title }))} onChange={setSelectedFormId} trigger="pill" />
          </label>
          <label>
            <span>Member</span>
            <Select ariaLabel="Member form assignment recipient" value={effectiveRecipientId} options={effectiveMembers.map((member) => ({ value: member.uid, label: `${member.name} · ${formatRoleLabel(t, member.role)}` }))} onChange={setSelectedRecipientId} trigger="pill" />
          </label>
          <Button variant="secondary" size="sm" disabled={!effectiveSelectedFormId || !effectiveRecipientId || sending} onClick={() => void send()}><SendRoundedIcon /> {sending ? 'Assigning...' : 'Assign'}</Button>
        </div>
      ) : <p className="memberControlNote">Viewers can submit assigned forms but cannot assign new requests.</p>}

      <div className="memberFormsColumns">
        <section>
          <span className="memberEyebrow">Assigned to me</span>
          <div className="memberRequestList">
            {assigned.length === 0 ? <p>No assigned forms.</p> : assigned.map((request) => (
              <Pressable key={request.id} className="memberRequestCard" onClick={() => request.status === 'PENDING' && setActiveRequest(request)}>
                <AssignmentRoundedIcon />
                <span><strong>{request.formTitle}</strong><small>{formatRequestStatus(request)} · {request.sourceItems?.map((source) => source.itemTitle).join(', ') || `from ${request.requestedByName}`}</small></span>
              </Pressable>
            ))}
          </div>
        </section>
        <section>
          <span className="memberEyebrow">Sent by me</span>
          <div className="memberRequestList">
            {effectiveSent.length === 0 ? <p>No sent forms.</p> : effectiveSent.map((request) => (
              <article key={request.id} className="memberRequestCard isStatic">
                <AssignmentRoundedIcon />
                <span><strong>{request.formTitle}</strong><small>{formatRequestStatus(request)} · to {request.recipientName}</small></span>
              </article>
            ))}
          </div>
        </section>
      </div>

      {activeRequest && rendererDefinition ? (
        <div className="memberFormOverlay" data-testid="member-form-renderer">
          <div className="memberFormOverlayHeader">
            <div><span className="memberEyebrow">Assigned form</span><h2>{rendererDefinition.title}</h2></div>
            <IconButton variant="tertiary" size="md" label="Close form" icon={<CloseRoundedIcon />} onClick={() => { setActiveFormDirty(false); setActiveRequest(null) }} />
          </div>
          <FormExperienceScreen
            definition={rendererDefinition}
            initialMode="FILL"
            canRespond={activeRequest.status === 'PENDING'}
            canReviewResponses={false}
            compact
            onBack={() => {
              setActiveFormDirty(false)
              setActiveRequest(null)
            }}
            onDirtyChange={setActiveFormDirty}
            onSubmitted={() => {
              setActiveFormDirty(false)
              setActiveRequest(null)
            }}
          />
        </div>
      ) : null}
    </section>
  )
}
